import {
  HomeAccountListingDetailsModel,
  InspectionModel,
  MaintenanceModel,
  PropertySummary,
  ReportDetailModel,
} from 'api/models';
import UserActionMaintenance from 'containercomponents/MaintenanceStatus/UserActionMaintenance/UserActionMaintenance';
import { USERACTIONMAINTENANCE_TYPES } from 'containercomponents/MaintenanceStatus/UserActionMaintenance/userActionMaintenanceTypes';
import { Text } from 'design-system';
import { find } from 'es-toolkit/compat';
import {
  ConsentStatus,
  EmployeeAssignmentTypes,
  MaintenanceRequester,
} from 'models/enums';
import { EMPLOYEE_TITLES } from 'strings/common.strings';

type GroupEmployeeAssignmentsProps = {
  report?: ReportDetailModel;
  task?: MaintenanceModel;
  listingsData: HomeAccountListingDetailsModel[];
  propertiesData: PropertySummary[];
  // Read as `inspectionData?.inspectedBy` below, and the maintenance modal
  // renders this without an inspection in scope.
  inspectionData?: InspectionModel;
};

export const GroupEmployeeAssignments = ({
  report,
  task,
  listingsData,
  propertiesData,
  inspectionData,
}: GroupEmployeeAssignmentsProps) => {
  // const assessor = report?.employeeAssignments?.find(
  //   (ea) => ea.employeeAssignment?.assignmentType === EmployeeAssignmentTypes.ImprovementsAssessor
  // );
  // Dropped the `|| {}` fallback in favour of optional reads: it existed only so
  // the properties below could be dotted, and `{}` has none of them.
  const reportDetails = report?.report;
  const maintenancePayment = report?.details?.group?.payments?.find(
    (p: any) => p.paidBy === MaintenanceRequester.Homeowner,
  );
  const homeowner = task?.homeowner;
  const approved =
    maintenancePayment?.consentStatus === ConsentStatus.Approved &&
    reportDetails?.completedOn &&
    homeowner;

  const listing = find(
    listingsData,
    (listingDetail) =>
      listingDetail.homeListingDetailsModel!.basicInfo!.unitId ===
      report?.report?.homeId,
  );
  const inspectedBy =
    inspectionData?.inspectedBy ||
    listing?.homeListingDetailsModel?.inspectedBy;

  let foundUnit: any;

  // `propertiesData` is already the property array, so reading `.properties` off
  // it is always `undefined` and this loop has never run — which leaves
  // `assessor` permanently undefined and the "assessed" block below unreachable.
  // Preserved exactly rather than corrected, since fixing it would surface a
  // section of the modal that has never rendered. See §4 of
  // docs/development/typecheck-baseline-followups.md.
  (propertiesData as any)?.properties?.forEach((p: any) => {
    const unit = p.units.find(
      (u: any) => u.basicInfo.unitId === report?.report?.homeId,
    );
    if (unit) {
      foundUnit = unit;
    }
  });

  const concierge = foundUnit?.employeeAssignments?.find(
    (ea: any) =>
      ea.employeeAssignment.assignmentType ===
      EmployeeAssignmentTypes.Concierge,
  );

  const homeownerGrowth = foundUnit?.employeeAssignments?.find(
    (ea: any) =>
      ea.employeeAssignment.assignmentType ===
      EmployeeAssignmentTypes.HomeownerGrowth,
  );

  const assessor = concierge ?? homeownerGrowth;
  const assessed = assessor && reportDetails?.publishedOn;

  return (
    <>
      <h3>Summary</h3>
      {}
      <Text className="mt-sm">"{task!.description}"</Text>
      {inspectedBy?.userId && inspectedBy.profileImageUrl && (
        <div className="pt-lg">
          <UserActionMaintenance
            userId={inspectedBy.userId}
            leftPadding
            withSpace={false}
            taskType={USERACTIONMAINTENANCE_TYPES.REPORTED}
            time={task!.createdOn}
            name={`${inspectedBy.firstName} ${inspectedBy.lastName}`}
            profileImage={inspectedBy.profileImageUrl}
            reportedBy="Inspector Extraordinaire"
            belongEmployee
            disableProfileClick
          />
        </div>
      )}

      {assessed && (
        <div className="pt-lg">
          <UserActionMaintenance
            userId={assessor.user.userId}
            leftPadding
            withSpace={false}
            taskType={USERACTIONMAINTENANCE_TYPES.ASSESSED}
            time={reportDetails?.publishedOn}
            name={assessor.user.name}
            profileImage={assessor.user.profileImageUrl}
            reportedBy={
              EMPLOYEE_TITLES[
                assessor.employeeAssignment
                  .assignmentType as keyof typeof EMPLOYEE_TITLES
              ]
            }
            belongEmployee
            disableProfileClick
          />
        </div>
      )}

      {approved && (
        <div className="pt-lg">
          <UserActionMaintenance
            userId={homeowner.userId}
            leftPadding
            reportedBy="Homeowner"
            withSpace={false}
            taskType={USERACTIONMAINTENANCE_TYPES.APPROVED}
            time={reportDetails?.completedOn}
            profileImage={homeowner.profileImageUrl}
            name="You"
            disableProfileClick
          />
        </div>
      )}
    </>
  );
};
